import React from 'react';
import { useTranslation } from 'react-i18next';

import { useModal } from '@private/modals';

import ModalContainer, { IModalContainerProps } from 'components/ModalContainer';
import { ICONS_MAP } from 'constants/icons';
import { Content, Message, Title } from 'modals/ConfirmActionModal/style';

export interface IConfirmModalConfig extends IModalContainerProps {
  cancelText?: string;
  confirmText?: string;
  countBackTime?: number;
  enableLoading?: boolean;
  fullWidth?: boolean;
  message?: React.ReactNode;
  onCancel?: () => void;
  onConfirm: () => void;
  onDismiss?: () => void;
  title?: string;
}

export interface IConfirmModalProps extends IConfirmModalConfig {
  closeModal: () => void;
}

const ConfirmModal = ({
  cancelText,
  closeModal,
  confirmText,
  countBackTime,
  enableLoading,
  fullWidth,
  message,
  onCancel,
  onConfirm,
  onDismiss,
  title,
  ...rest
}: IConfirmModalProps) => {
  const { t } = useTranslation('translation', { keyPrefix: 'groupEdit.confirmModal' });
  const [isLoading, setIsLoading] = React.useState(false);
  const [time, setTime] = React.useState(countBackTime);

  React.useEffect(() => {
    let timeout: NodeJS.Timeout;
    if (time) {
      timeout = setTimeout(() => setTime(time - 1), 1000);

      return () => clearTimeout(timeout);
    }
  }, [time]);

  const onRightAction = React.useCallback(async () => {
    if (enableLoading) {
      setIsLoading(true);
      await onConfirm();
      setIsLoading(false);
    } else {
      onConfirm();
    }
    closeModal();
  }, [closeModal, enableLoading, onConfirm]);

  const onLeftAction = React.useCallback(async () => {
    await onCancel?.();
    closeModal();
  }, [closeModal, onCancel]);

  const onDismissAction = React.useCallback(async () => {
    await onDismiss?.();
    closeModal();
  }, [closeModal, onDismiss]);

  return (
    <ModalContainer
      disableRightBtn={isLoading || !!time}
      leftBtnText={cancelText}
      onDismiss={onDismissAction}
      onLeftAction={onLeftAction}
      onRightAction={onRightAction}
      rightBtnLoading={isLoading}
      rightBtnText={time ? `${confirmText} (${time})` : confirmText}
      {...rest}
    >
      <Content>
        <ICONS_MAP.Confirm />
        <Title as='h3'>{title || t('title')}</Title>
        <Message $fullWidth={fullWidth} size='xl' variant='bold'>
          {message}
        </Message>
      </Content>
    </ModalContainer>
  );
};

type TUseConfirmModalResult = [(config: IConfirmModalConfig) => void, () => void];

export const useConfirmActionModal = (): TUseConfirmModalResult => {
  const { closeModal, showModal } = useModal();

  const showConfirmModal = React.useCallback(
    (config: IConfirmModalConfig) => {
      showModal(<ConfirmModal {...config} closeModal={closeModal} />);
    },
    [closeModal, showModal],
  );

  return [showConfirmModal, closeModal];
};
